import { DistanceType } from '../types/Distance';
import { StanceType } from '../types/Stance';
import { CommandType } from '../types/Command';
import { GAME_WIDTH } from './gameConfig';

/** バトル画面レイアウト定数 */
export interface BattleLayoutConfig {
  /** HPバーのY座標 */
  hpBarY: number;
  /** HPバーの幅 */
  hpBarWidth: number;
  /** HPバーの高さ */
  hpBarHeight: number;
  /** プレイヤーHPバーのX座標 */
  playerHpBarX: number;
  /** 敵HPバーのX座標 */
  enemyHpBarX: number;
  /** キャラ表示エリアのY座標 */
  characterY: number;
  /** ステータス表示エリアのY座標 */
  statusY: number;
  /** 残り時間表示のY座標 */
  timeY: number;
  /** コマンドカウントダウン表示のY座標 */
  countdownY: number;
  /** キャラクタースプライトの表示サイズ（px） */
  characterDisplaySize: number;
  /** ボスキャラクターの表示サイズ（px） */
  bossCharacterDisplaySize: number;
}

/** バトル画面レイアウト */
export const BATTLE_LAYOUT: BattleLayoutConfig = {
  hpBarY: 30,
  hpBarWidth: 300,
  hpBarHeight: 20,
  playerHpBarX: 30,
  enemyHpBarX: GAME_WIDTH - 30 - 300,
  characterY: 225,
  statusY: 387,
  timeY: 60,
  countdownY: 85,
  characterDisplaySize: 180,
  bossCharacterDisplaySize: 270,
};

/** コマンドカウントダウン秒数（サーバーの DEFAULT_COMMAND_TIMEOUT_MS と一致させること） */
export const COMMAND_COUNTDOWN_SECONDS = 30;

/** カウントダウン警告しきい値（残りこの秒数以下で赤色表示） */
export const COMMAND_COUNTDOWN_WARNING_THRESHOLD = 10;

/** バトル画面の色設定 */
export interface BattleColorConfig {
  /** プレイヤーHPバー色 */
  playerHpBar: number;
  /** 敵HPバー色 */
  enemyHpBar: number;
  /** HPバー背景色 */
  hpBarBg: number;
  /** 距離表示テキスト色 */
  distanceText: string;
  /** 残り時間テキスト色 */
  timeText: string;
  /** プレイヤースタンステキスト色 */
  playerStanceText: string;
  /** 敵スタンステキスト色 */
  enemyStanceText: string;
}

/** バトル画面色設定 */
export const BATTLE_COLORS: BattleColorConfig = {
  playerHpBar: 0x44cc44,
  enemyHpBar: 0xcc4444,
  hpBarBg: 0x333333,
  distanceText: '#ffff88',
  timeText: '#ffffff',
  playerStanceText: '#88ccff',
  enemyStanceText: '#ff8888',
};

/** 距離表示ラベル */
export const DISTANCE_LABELS: Record<DistanceType, string> = {
  [DistanceType.NEAR]: '近距離',
  [DistanceType.MID]: '中距離',
  [DistanceType.FAR]: '遠距離',
};

/** スタンス表示ラベル */
export const STANCE_LABELS: Record<StanceType, string> = {
  [StanceType.NORMAL]: '通常',
  [StanceType.OFFENSIVE]: '攻勢',
  [StanceType.DEFENSIVE]: '守勢',
};

/** バトル初期設定 */
export interface BattleInitialConfig {
  /** 初期距離 */
  initialDistance: DistanceType;
  /** 初期スタンス */
  initialStance: StanceType;
  /** 初期制限時間（秒） */
  initialTime: number;
}

/** バトル初期値 */
export const BATTLE_INITIAL: BattleInitialConfig = {
  initialDistance: DistanceType.MID,
  initialStance: StanceType.NORMAL,
  initialTime: 90,
};

/** 距離ごとのキャラクターX座標 */
export const DISTANCE_CHARACTER_POSITIONS: Record<
  DistanceType,
  { playerX: number; enemyX: number }
> = {
  [DistanceType.NEAR]: { playerX: 336, enemyX: 624 },
  [DistanceType.MID]: { playerX: 240, enemyX: 720 },
  [DistanceType.FAR]: { playerX: 144, enemyX: 816 },
};

/** コマンドボタンのデフォルトラベル（STANCE_A/Bはスタンスに応じて動的に変わる） */
export const COMMAND_LABELS: Record<CommandType, string> = {
  [CommandType.ADVANCE]: '前進',
  [CommandType.RETREAT]: '後退',
  [CommandType.WEAPON_ATTACK]: '武器',
  [CommandType.SPECIAL_ATTACK]: '特殊',
  [CommandType.REFLECTOR]: 'リフレクタ',
  [CommandType.STANCE_A]: 'スタンスA',
  [CommandType.STANCE_B]: 'スタンスB',
};

/** コマンド選択UIのレイアウト定数 */
export const COMMAND_UI_LAYOUT = {
  /** コマンドボタン1段目のY座標 */
  row1Y: 441,
  /** コマンドボタン2段目のY座標 */
  row2Y: 477,
  /** ボタン幅 */
  buttonWidth: 80,
  /** ボタン高さ */
  buttonHeight: 30,
  /** ボタン間隔（左端から次の左端まで） */
  buttonSpacing: 114,
  /** 1段目の開始X座標 */
  row1StartX: 130,
  /** 2段目の開始X座標 */
  row2StartX: 187,
  /** 選択表示のY座標 */
  selectionY: 508,
  /** 決定ボタンのY座標 */
  confirmY: 508,
  /** キャンセルボタンのX座標 */
  cancelX: 780,
  /** キャンセルボタンのY座標 */
  cancelY: 477,
} as const;

/** コマンドUIの色設定 */
export const COMMAND_UI_COLORS = {
  /** 有効なボタン背景 */
  buttonActive: 0x445588,
  /** 無効なボタン背景 */
  buttonDisabled: 0x333333,
  /** 選択済みボタン背景 */
  buttonSelected: 0x6688bb,
  /** ボタンテキスト（有効） */
  buttonTextActive: '#ffffff',
  /** ボタンテキスト（無効） */
  buttonTextDisabled: '#666666',
  /** 決定ボタン背景（有効） */
  confirmActive: 0x448844,
  /** 決定ボタン背景（無効） */
  confirmDisabled: 0x333333,
  /** キャンセルボタン背景 */
  cancelButton: 0x884444,
  /** 選択表示テキスト */
  selectionText: '#ffffff',
  /** コマンドボタン押下時の色（タッチフィードバック） */
  buttonPressed: 0x3366aa,
  /** 決定ボタン押下時の色（タッチフィードバック） */
  confirmPressed: 0x336633,
  /** キャンセルボタン押下時の色（タッチフィードバック） */
  cancelPressed: 0x663333,
  /** チュートリアルハイライトのストローク色 */
  tutorialHighlight: 0x44ddff,
} as const;

/** コマンドボタンの配置（行ごと） */
export const COMMAND_BUTTON_ROWS: CommandType[][] = [
  [CommandType.ADVANCE, CommandType.RETREAT, CommandType.WEAPON_ATTACK, CommandType.SPECIAL_ATTACK],
  [CommandType.REFLECTOR, CommandType.STANCE_A, CommandType.STANCE_B],
];

/** コマンドボタンレイアウトの型 */
export interface CommandButtonLayout {
  buttonWidth: number;
  buttonHeight: number;
  buttonSpacing: number;
  row1Y: number;
  row2Y: number;
  row1StartX: number;
  row2StartX: number;
  selectionY: number;
  confirmY: number;
  cancelX: number;
  cancelY: number;
}

/**
 * デバイスに応じたコマンドボタンレイアウトを返す
 * モバイルではタッチしやすい大きめサイズに調整
 */
export function getCommandButtonLayout(isMobile: boolean): CommandButtonLayout {
  if (!isMobile) {
    return {
      buttonWidth: COMMAND_UI_LAYOUT.buttonWidth,
      buttonHeight: COMMAND_UI_LAYOUT.buttonHeight,
      buttonSpacing: COMMAND_UI_LAYOUT.buttonSpacing,
      row1Y: COMMAND_UI_LAYOUT.row1Y,
      row2Y: COMMAND_UI_LAYOUT.row2Y,
      row1StartX: COMMAND_UI_LAYOUT.row1StartX,
      row2StartX: COMMAND_UI_LAYOUT.row2StartX,
      selectionY: COMMAND_UI_LAYOUT.selectionY,
      confirmY: COMMAND_UI_LAYOUT.confirmY,
      cancelX: COMMAND_UI_LAYOUT.cancelX,
      cancelY: COMMAND_UI_LAYOUT.cancelY,
    };
  }

  // モバイル用: ボタンを大きく、間隔を広げる（WCAG推奨44px以上）
  const buttonWidth = 110;
  const buttonHeight = 48;
  const buttonSpacing = 130;
  const row1Y = 432;
  const row2Y = 480;
  const row1StartX = 120;
  const row2StartX = 185;
  const selectionY = 510;
  const confirmY = 510;
  const cancelX = GAME_WIDTH - 160;
  const cancelY = 480;

  return {
    buttonWidth,
    buttonHeight,
    buttonSpacing,
    row1Y,
    row2Y,
    row1StartX,
    row2StartX,
    selectionY,
    confirmY,
    cancelX,
    cancelY,
  };
}

/** エフェクト再生用定数 */
export const EFFECT_CONFIG = {
  /** ダメージ数値の表示時間（ms） */
  damageNumberDuration: 1400,
  /** ダメージ数値の色 */
  damageNumberColor: '#ff4444',
  /** 武器攻撃エフェクトの時間（ms） */
  weaponAttackDuration: 840,
  /** 武器攻撃フラッシュ色 */
  weaponFlashColor: 0xffffff,
  /** 攻撃者ダッシュの時間（ms） */
  weaponDashDuration: 280,
  /** 特殊攻撃エフェクトの時間（ms） */
  specialAttackDuration: 1120,
  /** 特殊攻撃パルス色 */
  specialPulseColor: 0xaa44ff,
  /** 特殊攻撃プロジェクタイルのフライト時間（ms） */
  specialProjectileDuration: 490,
  /** 特殊攻撃プロジェクタイルの色 */
  specialProjectileColor: '#cc44ff',
  /** リフレクターエフェクトの時間（ms） */
  reflectorDuration: 1120,
  /** リフレクターシールド色 */
  reflectorShieldColor: 0x4488ff,
  /** リフレクターテキスト色 */
  reflectorTextColor: '#4488ff',
  /** 回避エフェクトの時間（ms） */
  evasionDuration: 980,
  /** 回避テキスト色 */
  evasionTextColor: '#aaaaaa',
  /** 距離移動アニメーションの時間（ms） */
  distanceMoveDuration: 840,
  /** スタンス変更エフェクトの時間（ms） */
  stanceChangeDuration: 980,
  /** 攻勢スタンスのティント色 */
  stanceOffensiveColor: 0xff6600,
  /** 守勢スタンスのティント色 */
  stanceDefensiveColor: 0x4488ff,
  /** 通常スタンスのティント色 */
  stanceNormalColor: 0xaaaaaa,
  /** リフレクター構えの表示時間（ms） */
  reflectorDeployDuration: 840,
  /** リフレクター構えテキスト色 */
  reflectorDeployTextColor: '#88bbff',
  /** 武器スラッシュの色 */
  slashColor: 0xffffff,
  /** 武器スラッシュの線幅 */
  slashLineWidth: 5,
  /** 武器スラッシュの範囲（片側） */
  slashRange: 50,
  /** 特殊攻撃光球の色 */
  orbColor: 0xcc44ff,
  /** 特殊攻撃光球の輝き色 */
  orbGlowColor: 0xff88ff,
  /** 特殊攻撃光球の外径 */
  orbOuterRadius: 24,
  /** 特殊攻撃光球の内径 */
  orbInnerRadius: 14,
  /** 反射光球の外径 */
  reflectOrbOuterRadius: 20,
  /** 反射光球の内径 */
  reflectOrbInnerRadius: 12,
  /** リフレクター残回数切れブロックの表示時間（ms） */
  reflectorBlockDuration: 840,
  /** リフレクター残回数切れブロックのテキスト色 */
  reflectorBlockTextColor: '#aabbcc',
  /** リフレクター残回数切れブロックのティント色 */
  reflectorBlockTintColor: 0x7799bb,
  // --- SPECIAL_REFLECT 反射アニメーション ---
  /** 反射アニメーション: 光球飛翔フェーズの時間（ms） */
  specialReflectProjectileDuration: 490,
  /** 反射アニメーション: 盾構えフェーズの時間（ms） */
  specialReflectShieldDuration: 420,
  /** 反射アニメーション: 光球跳ね返りフェーズの時間（ms） */
  specialReflectReturnDuration: 490,
  /** 反射アニメーション: 被弾フラッシュの時間（ms） */
  specialReflectHitDuration: 560,
  /** 反射光球の色（オレンジ系で通常と区別） */
  specialReflectOrbColor: 0xff6600,
  /** 反射光球の輝き色 */
  specialReflectOrbGlowColor: 0xffaa44,
  // --- SPECIAL_CHARGE_FIZZLE 溜め→崩れ演出 ---
  /** 溜め演出の時間（ms） */
  specialChargeDuration: 350,
  /** 溜め時のティント色 */
  specialChargeColor: 0xcc44ff,
  /** 崩れ演出の時間（ms） */
  specialChargeFizzleDuration: 350,
  // --- ヒットパーティクル＆カメラシェイク ---
  /** カメラシェイク強度（画面サイズの割合） */
  cameraShakeIntensity: 0.005,
  /** カメラシェイク時間の倍率（weaponAttackDurationに対する比率） */
  cameraShakeDurationRatio: 0.25,
  // --- リフレクターエフェクト強化 (#187) ---
  /** バリア展開パーティクルの数 */
  reflectorDeployParticleCount: 8,
  /** バリア展開パーティクルの色 */
  reflectorDeployParticleColor: 0x88bbff,
  /** バリア展開パーティクルの散布距離 */
  reflectorDeployParticleSpread: 50,
  /** 反射ヒットパーティクルの数 */
  reflectHitParticleCount: 10,
  /** 反射ヒットパーティクルの色 */
  reflectHitParticleColor: 0xff8844,
  /** 反射ヒットパーティクルの散布距離 */
  reflectHitParticleSpread: 60,
  /** ブロックパーティクルの数（反射成功より少なめ） */
  reflectorBlockParticleCount: 5,
  /** ブロックパーティクルの色 */
  reflectorBlockParticleColor: 0x7799bb,
  /** ブロックパーティクルの散布距離 */
  reflectorBlockParticleSpread: 35,
  /** パーティクルのフェードアウト時間（ms） */
  particleFadeDuration: 300,
  // --- カメラワーク・トゥイーン演出 (#190) ---
  /** ダメージ数値ポップアップの初期スケール */
  damageNumberPopScale: 1.5,
  /** ダメージ数値ポップアップ縮小アニメーション時間（ms） */
  damageNumberPopDuration: 150,
  /** 前進/後退時のカメラズームイン倍率 */
  advanceRetreatZoomIn: 1.05,
  /** 前進/後退時のカメラズームアニメーション時間（ms） */
  advanceRetreatZoomDuration: 200,
  /** スタンス切替テキストの初期スケール */
  stanceChangePopScale: 1.5,
  /** スタンス切替テキストポップアップ縮小時間（ms） */
  stanceChangePopDuration: 150,
} as const;

/** ドットインジケーターのレイアウト定数 */
export const INDICATOR_LAYOUT = {
  /** インジケーターのY座標（HPテキストの下） */
  y: 80,
  /** 特殊攻撃とリフレクターの行間 */
  lineGap: 22,
  /** フォントサイズ */
  fontSize: '18px',
} as const;

/** 観戦モード: 自動ターン進行の遅延時間（ms） */
export const SPECTATOR_AUTO_TURN_DELAY_MS = 1500;

/** 観戦モード用メッセージ */
export const SPECTATOR_MESSAGES = {
  /** 中断ボタンのラベル */
  interruptButtonLabel: '中断',
  /** 中断確認ダイアログのタイトル */
  confirmTitle: '観戦を中断しますか？',
  /** 中断確認ダイアログ本文 */
  confirmBody: 'バトルを終了してメニューに戻ります。',
} as const;

/** ギブアップ確認ダイアログのメッセージ */
export const SURRENDER_MESSAGES = {
  /** ギブアップボタンのラベル */
  buttonLabel: 'ギブアップ',
  /** 確認ダイアログのタイトル */
  confirmTitle: 'ギブアップしますか？',
  /** 確認ダイアログ本文（CPUモード） */
  confirmBody: '負けとなりバトルを終了します。',
  /** 確認ダイアログ本文（通信対戦モード） */
  onlineConfirmBody: '対戦相手の勝利となります。',
  /** 「はい」ボタンラベル */
  confirmYes: 'はい',
  /** 「いいえ」ボタンラベル */
  confirmNo: 'いいえ',
} as const;

/**
 * 秒数を「M:SS」形式にフォーマット
 */
export function formatTime(seconds: number): string {
  const clamped = Math.max(0, Math.floor(seconds));
  const min = Math.floor(clamped / 60);
  const sec = clamped % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

/**
 * HPを0〜maxHpの範囲にクランプ
 */
export function clampHp(hp: number, maxHp: number): number {
  return Math.max(0, Math.min(hp, maxHp));
}
